// ===========================================
// Barcode Matching Utilities for OCR Text
// ===========================================

import { ExtractedText, OCRMatch } from '../types/ocr';

/**
 * Calculates the Levenshtein (edit) distance between two strings
 * Lower distance = more similar
 * @param a - First string
 * @param b - Second string
 * @returns Number of single-character edits needed to transform a into b
 */
export const editDistance = (a: string, b: string): number => {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  // Create distance matrix
  const matrix: number[][] = [];

  // Initialize first column
  for (let i = 0; i <= a.length; i++) {
    matrix[i] = [i];
  }

  // Initialize first row
  for (let j = 0; j <= b.length; j++) {
    matrix[0][j] = j;
  }

  // Fill in the rest of the matrix
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,     // deletion
        matrix[i][j - 1] + 1,     // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }

  return matrix[a.length][b.length];
};

/**
 * Calculates similarity score between two strings (0-1)
 * @param a - First string
 * @param b - Second string
 * @returns Similarity score (1 = identical, 0 = completely different)
 */
export const stringSimilarity = (a: string, b: string): number => {
  const maxLength = Math.max(a.length, b.length);
  if (maxLength === 0) return 1;

  const distance = editDistance(a, b);
  return 1 - distance / maxLength;
};

/**
 * Extracts potential barcode patterns from OCR text
 * Barcodes are typically numeric sequences of specific lengths
 * @param text - Raw OCR text
 * @param minLength - Minimum barcode length (default 8)
 * @param maxLength - Maximum barcode length (default 15)
 * @returns Array of extracted digit sequences
 */
export const extractBarcodePatterns = (
  text: string,
  minLength: number = 8,
  maxLength: number = 15
): string[] => {
  // Remove common OCR noise and normalize
  const cleanText = text
    .replace(/[oO]/g, '0') // O often misread as 0
    .replace(/[lI|]/g, '1') // l, I, | often misread as 1
    .replace(/[sS]/g, '5') // S sometimes misread as 5
    .replace(/[bB]/g, '8') // B sometimes misread as 8
    .replace(/\s+/g, ' '); // Normalize whitespace

  const patterns: string[] = [];

  // Pattern 1: Pure digit sequences
  const pureDigits = cleanText.match(/\d{8,15}/g) || [];
  patterns.push(...pureDigits);

  // Pattern 2: Digits with common separators (dash, space, dot)
  const separatedPatterns = cleanText.match(/\d[\d\s\-.]{7,17}\d/g) || [];
  for (const pattern of separatedPatterns) {
    const digitsOnly = pattern.replace(/[\s\-.]/g, '');
    if (digitsOnly.length >= minLength && digitsOnly.length <= maxLength) {
      patterns.push(digitsOnly);
    }
  }

  // Pattern 3: Try to find sequences even with some noise
  // Split by whitespace and look for digit-heavy segments
  const words = cleanText.split(/\s+/);
  for (const word of words) {
    const digitsOnly = word.replace(/\D/g, '');
    if (digitsOnly.length >= minLength && digitsOnly.length <= maxLength) {
      // Only include if >80% digits
      if (digitsOnly.length / word.length > 0.8) {
        patterns.push(digitsOnly);
      }
    }
  }

  // Remove duplicates and return
  return Array.from(new Set(patterns));
};

/**
 * Normalizes text for comparison: lowercases, removes extra spaces/dashes/dots
 */
const normalizeForComparison = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[\s\-._]+/g, '') // Remove separators
    .trim();
};

/**
 * Extracts all meaningful text tokens from OCR output
 * Unlike extractBarcodePatterns (digit-only), this returns all tokens
 * that could be box numbers or identifiers (alphanumeric)
 */
const extractTextTokens = (
  text: string,
  minLength: number = 3
): string[] => {
  const tokens: string[] = [];

  // Split by whitespace and newlines
  const words = text.split(/[\s\n\r]+/).filter(Boolean);

  for (const word of words) {
    const cleaned = word.replace(/[^a-zA-Z0-9\-_.]/g, '').trim();
    if (cleaned.length >= minLength) {
      tokens.push(cleaned);
    }
  }

  // Also try the full text (without newlines) as a single token
  const fullCleaned = text.replace(/[^a-zA-Z0-9\-_.\s]/g, '').trim();
  if (fullCleaned.length >= minLength) {
    tokens.push(fullCleaned);
  }

  // Also extract any contiguous alphanumeric sequences
  const alphanumeric = text.match(/[a-zA-Z0-9\-_.]{3,}/g) || [];
  tokens.push(...alphanumeric);

  return Array.from(new Set(tokens));
};

/**
 * Finds best matches between extracted texts and expected barcodes/box numbers
 * Supports both numeric barcodes and alphanumeric text identifiers
 * @param texts - Array of extracted text blocks from OCR
 * @param expectedBarcodes - Array of expected barcode/box-number strings
 * @param maxEditDistance - Maximum edit distance to consider a match (default 2)
 * @returns Array of matches sorted by confidence
 */
export const findBarcodeMatches = (
  texts: ExtractedText[],
  expectedBarcodes: string[],
  maxEditDistance: number = 2
): OCRMatch[] => {
  const matches: OCRMatch[] = [];
  const seenKeys = new Set<string>(); // track barcode+matchedText to avoid dupes

  const addMatch = (match: OCRMatch) => {
    const key = `${match.barcode}::${match.matchedText}`;
    if (!seenKeys.has(key)) {
      seenKeys.add(key);
      matches.push(match);
    }
  };

  // ---- Strategy 1: Direct text matching (handles alphanumeric box numbers) ----
  for (const textBlock of texts) {
    // Extract all meaningful tokens from the OCR text
    const tokens = extractTextTokens(textBlock.text);

    for (const token of tokens) {
      const normalizedToken = normalizeForComparison(token);

      for (const barcode of expectedBarcodes) {
        const normalizedBarcode = normalizeForComparison(barcode);

        // Exact match (case-insensitive, separator-insensitive)
        if (normalizedToken === normalizedBarcode) {
          addMatch({
            barcode,
            matchedText: token,
            confidence: 1,
            editDistance: 0,
            isExactMatch: true,
          });
          continue;
        }

        // Substring match: the barcode appears within the extracted text
        if (
          normalizedToken.includes(normalizedBarcode) ||
          normalizedBarcode.includes(normalizedToken)
        ) {
          const lenDiff = Math.abs(normalizedToken.length - normalizedBarcode.length);
          if (lenDiff <= maxEditDistance) {
            addMatch({
              barcode,
              matchedText: token,
              confidence: Math.max(textBlock.confidence ?? 0, 0.8),
              editDistance: lenDiff,
              isExactMatch: false,
            });
            continue;
          }
        }

        // Edit distance on normalized text
        const distance = editDistance(normalizedToken, normalizedBarcode);
        if (distance <= maxEditDistance) {
          addMatch({
            barcode,
            matchedText: token,
            confidence: Math.max(
              1 - (distance / Math.max(normalizedToken.length, normalizedBarcode.length)),
              textBlock.confidence ?? 0
            ),
            editDistance: distance,
            isExactMatch: false,
          });
        } else if (distance === maxEditDistance + 1 && normalizedBarcode.length >= 6) {
          // Extended tier: low-confidence match for longer barcodes
          addMatch({
            barcode,
            matchedText: token,
            confidence: 0.3,
            editDistance: distance,
            isExactMatch: false,
          });
        }
      }
    }
  }

  // ---- Strategy 2: Numeric barcode pattern extraction (original approach) ----
  const allText = texts.map((t) => t.text).join(' ');
  const extractedPatterns = extractBarcodePatterns(allText);

  for (const textBlock of texts) {
    const blockPatterns = extractBarcodePatterns(textBlock.text);
    extractedPatterns.push(...blockPatterns);
  }

  const uniquePatterns = Array.from(new Set(extractedPatterns));

  for (const pattern of uniquePatterns) {
    for (const barcode of expectedBarcodes) {
      // Normalize both to base-12 so 12/13-digit barcodes compare correctly
      const distance = editDistance(normalizeBarcode(pattern), normalizeBarcode(barcode));

      if (distance <= maxEditDistance) {
        const relevantText = texts.find(
          (t) =>
            t.text.includes(pattern) ||
            extractBarcodePatterns(t.text).includes(pattern)
        );

        addMatch({
          barcode,
          matchedText: pattern,
          confidence: distance === 0 ? 1 : Math.max(
            1 - (distance / Math.max(normalizeBarcode(pattern).length, normalizeBarcode(barcode).length)),
            relevantText?.confidence ?? 0
          ),
          editDistance: distance,
          isExactMatch: distance === 0,
        });
      }
    }
  }

  // Sort by: exact matches first, then by edit distance, then by confidence
  matches.sort((a, b) => {
    if (a.isExactMatch !== b.isExactMatch) {
      return a.isExactMatch ? -1 : 1;
    }
    if (a.editDistance !== b.editDistance) {
      return a.editDistance - b.editDistance;
    }
    return b.confidence - a.confidence;
  });

  // Keep best match per barcode
  const uniqueMatches: OCRMatch[] = [];
  const seenBarcodes = new Set<string>();

  for (const match of matches) {
    if (!seenBarcodes.has(match.barcode)) {
      seenBarcodes.add(match.barcode);
      uniqueMatches.push(match);
    }
  }

  return uniqueMatches;
};

/**
 * Quick check if a string looks like a barcode
 * @param text - Text to check
 * @returns True if text appears to be a barcode
 */
export const looksLikeBarcode = (text: string): boolean => {
  const digitsOnly = text.replace(/\D/g, '');

  // Check if it's a reasonable barcode length with mostly digits
  return (
    digitsOnly.length >= 8 &&
    digitsOnly.length <= 15 &&
    digitsOnly.length / text.length > 0.7
  );
};

/**
 * Normalizes a barcode string by removing non-digit characters
 * and stripping the EAN-13 check digit (13→12 digits) so that
 * 12-digit and 13-digit representations of the same barcode compare equal.
 * @param barcode - Raw barcode string
 * @returns Normalized barcode (digits only, base-12 when applicable)
 */
export const normalizeBarcode = (barcode: string): string => {
  const digits = barcode.replace(/\D/g, '');
  if (digits.length === 13) return digits.slice(0, 12);
  return digits;
};

/**
 * Validates if extracted text matches a specific barcode
 * @param extractedText - Text extracted from OCR
 * @param expectedBarcode - Expected barcode to match against
 * @param maxEditDistance - Maximum allowed edit distance
 * @returns Match result or null if no match
 */
export const validateBarcodeMatch = (
  extractedText: string,
  expectedBarcode: string,
  maxEditDistance: number = 2
): OCRMatch | null => {
  const patterns = extractBarcodePatterns(extractedText);

  for (const pattern of patterns) {
    // Normalize both to base-12 so 12/13-digit barcodes compare correctly
    const normalizedPattern = normalizeBarcode(pattern);
    const normalizedExpected = normalizeBarcode(expectedBarcode);
    const distance = editDistance(normalizedPattern, normalizedExpected);
    if (distance <= maxEditDistance) {
      return {
        barcode: expectedBarcode,
        matchedText: pattern,
        confidence: distance === 0 ? 1 : 1 - distance / normalizedExpected.length,
        editDistance: distance,
        isExactMatch: distance === 0,
      };
    }
  }

  return null;
};

/**
 * Gets the best single match from OCR text for a list of expected barcodes
 * @param texts - Array of extracted text blocks
 * @param expectedBarcodes - Array of expected barcodes
 * @returns Best match or null if none found
 */
export const getBestMatch = (
  texts: ExtractedText[],
  expectedBarcodes: string[]
): OCRMatch | null => {
  const matches = findBarcodeMatches(texts, expectedBarcodes);
  return matches.length > 0 ? matches[0] : null;
};
